import type { LucideIcon } from 'lucide-react';
import {
  BrainIcon,
  CodeIcon,
  DownloadIcon,
  FileTextIcon,
  FolderArchiveIcon,
  PaperclipIcon,
  SearchIcon,
  WrenchIcon,
  ZapIcon,
} from 'lucide-react';
import { Fragment } from 'react';
import type { ReactNode } from 'react';

import { getDataExplorerSourceMetadata, type SourcesMap } from './sources';
import { formatSkillCommand } from '../../../lib/skill-command-utils';
import { isToolHandoffName } from '../../../lib/tool-handoff';

export type ReasoningState = 'streaming' | 'complete';
export type ToolInvocationState = 'input-streaming' | 'input-available' | 'output-available';

type CoTStepPosition = {
  /** Original message.parts index. Synthetic streaming steps use parts.length. */
  partIndex?: number;
  /** Position within the complete CoT step list for stable keys/progress lookup. */
  stepIndex?: number;
};

export type CoTReasoningStep = CoTStepPosition & {
  type: 'reasoning';
  text: string;
  title?: string;
  state?: ReasoningState;
};
export type CoTToolStep = {
  type: 'tool';
  toolName: string;
  label: ReactNode;
  state?: ToolInvocationState;
  input?: unknown;
  output?: unknown;
} & CoTStepPosition;
export type CoTStep = CoTReasoningStep | CoTToolStep;

type CoTStepBlockSummaryPart = {
  key: string;
  count: number;
  firstIndex: number;
  skillHandles?: string[];
};

export type CoTStepBlockRenderItem =
  | {
      type: 'block';
      blocks: CoTStep[][];
    }
  | {
      type: 'group';
      blocks: CoTStep[][];
      icon: LucideIcon;
      title: ReactNode;
    };

export type CoTStepBlockGroupingMode = 'interleaved' | 'standalone';

const INTERLEAVED_GROUP_THRESHOLD = 4;
const STANDALONE_SPLIT_THRESHOLD = 7;
const MAX_GROUPED_BLOCK_COUNT = 10;

export function getCoTStepStatus(step: CoTStep): 'active' | 'complete' {
  if (step.type === 'reasoning') {
    return step.state === 'streaming' ? 'active' : 'complete';
  }
  return step.state === 'output-available' ? 'complete' : 'active';
}

/** Strip leading **Title** from reasoning text to avoid duplicating the step label. */
export function stripLeadingTitle(text: string): string {
  return text.replace(/^\*\*(.+?)\*\*\s*\n?/, '').trim();
}

export const TOOL_LABELS: Record<string, string> = {
  browserUse: 'Browsing…',
  checkCapability: 'Thinking...',
  coverageGapAnalysis: 'Analyzing coverage gaps...',
  dataExplorer: 'Searching Navigator...',
  image_generation: 'Generating image...',
  web_search: 'Searching the web...',
  processFiles: 'Processing files...',
  emailDraftReview: 'Preparing email draft...',
  summarizeEntities: 'Summarizing...',
  fillSl1Form: 'Filling SL1 form...',
  fillSl2Form: 'Filling SL2 form...',
  formFilling: 'Filling form...',
  pptxRevisor: 'Revising content...',
  documentSearch: 'Searching documents...',
  listFiles: 'Searching files...',
  readFile: 'Reading file...',
  getMessage: 'Reading email...',
  downloadEmailAttachments: 'Downloading attachments...',
  listInbox: 'Searching inbox...',
  listUnifiedMail: 'Searching mail...',
  loadSkill: 'Loading skill...',
  shell: 'Running command...',
  exposeFileToUser: 'Preparing file...',
  stageFileForShell: 'Preparing file for review...',
  clarification: 'Waiting for your input...',
};

export function getToolLabel(toolName: string, input?: unknown): ReactNode {
  if (toolName === 'loadSkill') {
    const command = getField<string>(input, 'command');
    const legacySkillId = getField<string>(input, 'skillId');
    const skillHandle = formatSkillCommand(command) || legacySkillId;
    if (skillHandle) {
      return (
        <>
          Using <code className="bg-muted rounded px-1 py-0.5 text-xs">{skillHandle}</code> skill
        </>
      );
    }
    return TOOL_LABELS.loadSkill;
  }
  if (toolName === 'shell') {
    const commands = getField<string[]>(input, 'action', 'commands');
    if (commands && commands.length > 1) {
      return <>Running {commands.length} commands</>;
    }
    return TOOL_LABELS.shell;
  }
  if (toolName === 'exposeFileToUser') {
    return getExposeFilePreparingLabel(getField(input, 'kind'));
  }
  if (toolName === 'stageFileForShell') {
    return 'Preparing file for review...';
  }
  return TOOL_LABELS[toolName] ?? `Running ${toolName}...`;
}

export function getCoTStepIcon(step: CoTStep): LucideIcon {
  if (step.type === 'reasoning') return BrainIcon;
  if (step.toolName === 'loadSkill') return ZapIcon;
  if (step.toolName === 'shell') return CodeIcon;
  if (step.toolName === 'exposeFileToUser' && getExposeFileKind(step.input ?? step.output) === 'folder') {
    return FolderArchiveIcon;
  }
  if (step.toolName === 'exposeFileToUser') return DownloadIcon;
  if (step.toolName === 'stageFileForShell') return PaperclipIcon;
  if (step.toolName === 'readFile') return FileTextIcon;
  if (step.toolName === 'dataExplorer' || step.toolName === 'documentSearch' || step.toolName === 'listFiles') {
    return SearchIcon;
  }
  return WrenchIcon;
}

export function getCoTStepLabel(step: CoTStep, sourcesMap?: SourcesMap): ReactNode {
  if (step.type === 'reasoning') return step.title || 'Thinking...';
  if (getCoTStepStatus(step) === 'complete') return getCompletedCoTToolStepTitle(step, sourcesMap);
  return step.label;
}

function getCompletedCoTToolStepTitle(step: CoTToolStep, sourcesMap?: SourcesMap): ReactNode {
  if (step.toolName === 'loadSkill') return step.label;
  if (step.toolName === 'shell') return getCoTStepStatus(step) === 'complete' ? 'Ran command' : 'Running command';
  if (step.toolName === 'exposeFileToUser') return getCompletedExposeFileTitle(step);
  if (step.toolName === 'stageFileForShell') return 'Prepared file for review';
  if (step.toolName === 'web_search') return 'Searched the web';
  if (step.toolName === 'dataExplorer') return getCompletedDataExplorerTitle(step.output, sourcesMap);
  if (step.toolName === 'processFiles') return 'Processed files';
  if (step.toolName === 'documentSearch') return 'Searched documents';
  if (step.toolName === 'listFiles') return getCompletedListFilesTitle(step.output, step.input);
  if (step.toolName === 'readFile') return 'Read file';
  if (step.toolName === 'getMessage') return 'Read email';
  if (step.toolName === 'listInbox' || step.toolName === 'listUnifiedMail') return 'Searched mail';
  if (step.toolName === 'clarification') return 'Asked for clarification';

  return step.label;
}

function getCompletedExposeFileTitle(step: CoTToolStep): ReactNode {
  const filename = getField<string>(step.output, 'filename') ?? getField<string>(step.input, 'filename');
  const kind = getExposeFileKind(step.output ?? step.input);

  if (!filename) return kind === 'folder' ? 'Prepared folder' : 'Prepared file';

  return (
    <>
      Prepared {kind === 'folder' ? 'folder ' : null}
      <strong>{filename}</strong>
    </>
  );
}

type ExposeFileKind = 'skill' | 'file' | 'folder';

function getExposeFileKind(value: unknown): ExposeFileKind {
  const kind = getField<unknown>(value, 'kind');
  if (kind === 'skill' || kind === 'folder') return kind;
  return 'file';
}

export function getExposeFilePreparingLabel(kind: unknown): string {
  if (kind === 'skill') return 'Preparing skill bundle...';
  if (kind === 'folder') return 'Preparing folder...';
  return 'Preparing download...';
}

export function getExposeFileReadyLabel(kind: unknown): string {
  if (kind === 'skill') return 'Ready to save as a skill';
  if (kind === 'folder') return 'Folder ready to download';
  return 'Ready to download';
}

function getCompletedListFilesTitle(output: unknown, input: unknown): string {
  const totalFiles = getField<number>(output, 'totalFiles');
  const files = getField<unknown[]>(output, 'files');
  const count = totalFiles ?? files?.length;
  const { fileType, filters } = getListFilesTitleDetails(input);
  const fileLabel = `${fileType ? `${fileType} ` : ''}${count === 1 ? 'file' : 'files'}`;
  const filterLabel = filters.length ? ` ${filters.join(' ')}` : '';

  if (count == null) return 'Searched files';
  if (count === 0) return `Found no ${fileType ? `${fileType} files` : 'files'}${filterLabel}`;
  return `Found ${count} ${fileLabel}${filterLabel}`;
}

function getListFilesTitleDetails(input: unknown): { fileType?: string; filters: string[] } {
  const categories = getField<string[]>(input, 'categories') ?? [];
  const filenameSearch = getField<string>(input, 'filenameSearch');
  const objectReferenceEntityType = getField<string>(input, 'objectReference', 'entityType');
  const fileType = categories.length === 1 ? normalizeListFilesCategory(categories[0]) : undefined;
  const normalizedReferenceType = objectReferenceEntityType
    ? (normalizeEntityType(objectReferenceEntityType) ?? objectReferenceEntityType)
    : undefined;

  const filters = [
    normalizedReferenceType && normalizedReferenceType !== fileType
      ? `for ${formatEntityTypeLabel(normalizedReferenceType, 1)}`
      : undefined,
    filenameSearch ? `matching "${filenameSearch}"` : undefined,
  ].filter((part): part is string => !!part);

  return { fileType, filters };
}

function normalizeListFilesCategory(category: string): string {
  return category.trim().toLowerCase().replace(/[_-]+/g, ' ');
}

function getCompletedDataExplorerTitle(output: unknown, sourcesMap?: SourcesMap): string {
  const outputSources = getField<Array<{ sourceId?: string }>>(output, 'sources') ?? [];
  const entityTypes = outputSources
    .map((sourceRef) => {
      if (!sourceRef.sourceId) return undefined;
      const source = sourcesMap?.get(sourceRef.sourceId);
      return source ? getDataExplorerSourceMetadata(source)?.entityType : undefined;
    })
    .filter((entityType): entityType is string => !!entityType);
  const rowCount = getField<number>(output, 'data', 'rowCount') ?? entityTypes.length;

  const countsByType = new Map<string, number>();

  for (const entityType of entityTypes) {
    const normalizedType = normalizeEntityType(entityType);
    if (!normalizedType || normalizedType === 'entity') {
      return 'Accessed some information in Navigator';
    }
    countsByType.set(normalizedType, (countsByType.get(normalizedType) ?? 0) + 1);
  }

  if (countsByType.size === 0) {
    return rowCount ? 'Accessed some information in Navigator' : 'Searched Navigator';
  }

  const countLabel = [...countsByType.entries()]
    .map(([entityType, count]) => `${count} ${formatEntityTypeLabel(entityType, count)}`)
    .join(' and ');

  return `Accessed ${countLabel} in Navigator`;
}

function normalizeEntityType(entityType: string): string | undefined {
  const normalized = entityType.trim().toLowerCase();
  return normalized || undefined;
}

function formatEntityTypeLabel(entityType: string, count: number): string {
  const label = entityType.replace(/[_-]+/g, ' ');
  if (count === 1) return label;

  const words = label.split(/\s+/);
  const lastWord = words.at(-1);
  if (!lastWord) return label;

  return [...words.slice(0, -1), pluralizeWord(lastWord)].join(' ');
}

function pluralizeWord(word: string): string {
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}

export function getCoTStepKey(step: CoTStep, index: number): string {
  if (step.type === 'reasoning') return `reasoning-${index}`;
  return `tool-${step.toolName}-${index}`;
}

export function getCoTStepSegmentBeforePart(steps: CoTStep[], nextStepIndex: number, partIndex: number): CoTStep[] {
  return steps.filter((step) => {
    const stepIndex = step.stepIndex ?? 0;
    const stepPartIndex = step.partIndex ?? Number.MAX_SAFE_INTEGER;
    return stepIndex >= nextStepIndex && stepPartIndex < partIndex;
  });
}

export function isFinalAnswerTextPart(part: { type: string; providerMetadata?: unknown }): boolean {
  if (part.type !== 'text') return false;

  const openAiMetadata = getField<Record<string, unknown>>(part.providerMetadata, 'openai');
  return openAiMetadata?.phase === 'final_answer';
}

export function getCoTStepBlocks(steps: CoTStep[]): CoTStep[][] {
  return steps.map((step) => [step]);
}

export function getCoTStepBlockRenderItems(
  blocks: CoTStep[][],
  { activeStepIndex, mode }: { activeStepIndex?: number; mode: CoTStepBlockGroupingMode },
): CoTStepBlockRenderItem[] {
  if (blocks.length === 0) return [];

  const activeBlockIndex =
    activeStepIndex == null
      ? -1
      : blocks.findIndex((block) => block.some((step) => step.stepIndex === activeStepIndex));

  if (activeBlockIndex >= 0) {
    return [
      ...getCoTStepBlockRenderItems(blocks.slice(0, activeBlockIndex), { mode }),
      { type: 'block' as const, blocks: [blocks[activeBlockIndex]] },
      ...getCoTStepBlockRenderItems(blocks.slice(activeBlockIndex + 1), { mode }),
    ];
  }

  const handoffIndex = blocks.findIndex(isToolHandoffBlock);
  if (handoffIndex >= 0) {
    if (mode === 'standalone' && blocks.length >= STANDALONE_SPLIT_THRESHOLD) {
      return [
        ...createStandaloneSeparatorSectionItems(blocks.slice(0, handoffIndex)),
        { type: 'block' as const, blocks: [blocks[handoffIndex]] },
        ...createStandaloneSeparatorSectionItems(blocks.slice(handoffIndex + 1)),
      ];
    }

    return [
      ...getCoTStepBlockRenderItems(blocks.slice(0, handoffIndex), { mode }),
      { type: 'block' as const, blocks: [blocks[handoffIndex]] },
      ...getCoTStepBlockRenderItems(blocks.slice(handoffIndex + 1), { mode }),
    ];
  }

  if (mode === 'interleaved') {
    if (blocks.length <= INTERLEAVED_GROUP_THRESHOLD) {
      return blocks.map((block) => ({ type: 'block', blocks: [block] }));
    }

    return createCoTStepBlockGroups(blocks);
  }

  if (blocks.length < STANDALONE_SPLIT_THRESHOLD) {
    return blocks.map((block) => ({ type: 'block', blocks: [block] }));
  }

  const pivotIndex = getStandaloneReasoningPivotIndex(blocks);

  if (pivotIndex == null) {
    return blocks.map((block) => ({ type: 'block', blocks: [block] }));
  }

  return [
    createCoTStepBlockRenderItem(blocks.slice(0, pivotIndex)),
    { type: 'block', blocks: [blocks[pivotIndex]] },
    createCoTStepBlockRenderItem(blocks.slice(pivotIndex + 1)),
  ].filter((item): item is CoTStepBlockRenderItem => item != null);
}

export function summarizeCoTStepBlocks(blocks: CoTStep[][]): { title: ReactNode; icon: LucideIcon } {
  const summaryParts = getCoTStepBlockSummaryParts(blocks);

  if (summaryParts.length === 0) {
    return { title: 'Worked through steps', icon: BrainIcon };
  }

  const skillPart = summaryParts.find((part) => part.key === 'loadSkill');
  const selectedParts = skillPart
    ? [
        skillPart,
        ...summaryParts
          .filter((part) => part.key !== 'loadSkill')
          .sort((a, b) => b.count - a.count || a.firstIndex - b.firstIndex)
          .slice(0, 1),
      ]
    : [...summaryParts]
        .sort((a, b) => b.count - a.count || a.firstIndex - b.firstIndex)
        .slice(0, 2)
        .sort((a, b) => a.firstIndex - b.firstIndex);

  const titleParts = selectedParts.map((part, index) => formatCoTStepBlockSummaryPart(part, index === 0));
  const hasMore = summaryParts.length > selectedParts.length;
  const firstIconStep = blocks[selectedParts[0]?.firstIndex ?? 0]?.[0];

  return {
    title: renderSummaryTitle(titleParts, hasMore),
    icon: firstIconStep ? getCoTStepIcon(firstIconStep) : BrainIcon,
  };
}

function createCoTStepBlockRenderItem(blocks: CoTStep[][]): CoTStepBlockRenderItem | null {
  if (blocks.length === 0) return null;
  if (blocks.length === 1) return { type: 'block', blocks };
  return createCoTStepBlockGroups(blocks)[0] ?? null;
}

function createStandaloneSeparatorSectionItems(blocks: CoTStep[][]): CoTStepBlockRenderItem[] {
  const handoffIndex = blocks.findIndex(isToolHandoffBlock);

  if (handoffIndex >= 0) {
    return [
      ...createStandaloneSeparatorSectionItems(blocks.slice(0, handoffIndex)),
      { type: 'block' as const, blocks: [blocks[handoffIndex]] },
      ...createStandaloneSeparatorSectionItems(blocks.slice(handoffIndex + 1)),
    ];
  }

  const item = createCoTStepBlockRenderItem(blocks);
  return item ? [item] : [];
}

function createCoTStepBlockGroups(blocks: CoTStep[][]): CoTStepBlockRenderItem[] {
  if (blocks.length === 0) return [];
  if (blocks.length <= MAX_GROUPED_BLOCK_COUNT) return [createCoTStepBlockGroup(blocks)];

  const pivotIndex = getNearestReasoningIndex(blocks, (blocks.length - 1) / 2);

  if (pivotIndex == null || pivotIndex <= 0 || pivotIndex >= blocks.length) {
    return [createCoTStepBlockGroup(blocks)];
  }

  return [
    ...createCoTStepBlockGroups(blocks.slice(0, pivotIndex)),
    ...createCoTStepBlockGroups(blocks.slice(pivotIndex)),
  ];
}

function createCoTStepBlockGroup(blocks: CoTStep[][]): CoTStepBlockRenderItem {
  const summary = summarizeCoTStepBlocks(blocks);
  return {
    type: 'group',
    blocks,
    icon: summary.icon,
    title: summary.title,
  };
}

function getStandaloneReasoningPivotIndex(blocks: CoTStep[][]): number | null {
  return getNearestReasoningIndex(blocks, (blocks.length - 1) / 2);
}

function getNearestReasoningIndex(blocks: CoTStep[][], targetIndex: number): number | null {
  const reasoningIndexes = blocks.reduce<number[]>((indexes, block, index) => {
    if (block[0]?.type === 'reasoning') {
      indexes.push(index);
    }
    return indexes;
  }, []);

  if (reasoningIndexes.length === 0) return null;

  return reasoningIndexes.reduce((bestIndex, index) => {
    const bestDistance = Math.abs(bestIndex - targetIndex);
    const distance = Math.abs(index - targetIndex);
    if (distance < bestDistance) return index;
    if (distance === bestDistance && index > bestIndex) return index;
    return bestIndex;
  }, reasoningIndexes[0]);
}

function isToolHandoffBlock(block: CoTStep[]): boolean {
  const firstStep = block[0];
  return firstStep?.type === 'tool' && isToolHandoffName(firstStep.toolName);
}

function getCoTStepBlockSummaryParts(blocks: CoTStep[][]): CoTStepBlockSummaryPart[] {
  const partsByKey = new Map<string, CoTStepBlockSummaryPart>();

  blocks.forEach((block, blockIndex) => {
    const firstStep = block[0];
    if (!firstStep) return;

    const key = firstStep.type === 'reasoning' ? 'reasoning' : firstStep.toolName;
    const skillHandle =
      firstStep.type === 'tool' && firstStep.toolName === 'loadSkill' ? getSkillHandle(firstStep) : undefined;
    const existing = partsByKey.get(key);

    if (existing) {
      existing.count += 1;
      if (skillHandle && !existing.skillHandles?.includes(skillHandle)) {
        existing.skillHandles = [...(existing.skillHandles ?? []), skillHandle];
      }
      return;
    }

    partsByKey.set(key, {
      key,
      count: 1,
      firstIndex: blockIndex,
      ...(skillHandle ? { skillHandles: [skillHandle] } : {}),
    });
  });

  return [...partsByKey.values()];
}

function renderSummaryTitle(parts: ReactNode[], hasMore: boolean): ReactNode {
  if (parts.every((part) => typeof part === 'string')) {
    return `${parts.join(', ')}${hasMore ? ' and more' : ''}`;
  }

  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? ', ' : null}
          {part}
        </Fragment>
      ))}
      {hasMore ? ' and more' : null}
    </>
  );
}

function formatCoTStepBlockSummaryPart(part: CoTStepBlockSummaryPart, sentenceStart: boolean): ReactNode {
  if (part.key === 'loadSkill') return getSkillSummaryPhrase(part, sentenceStart);

  const phrase = getCoTStepBlockSummaryPhrase(part);
  return sentenceStart ? capitalizeFirst(phrase) : phrase;
}

function getCoTStepBlockSummaryPhrase(part: CoTStepBlockSummaryPart): string {
  const { key, count } = part;
  if (key === 'reasoning') return `reasoned through ${count} ${count === 1 ? 'step' : 'steps'}`;
  if (key === 'shell') return `ran ${count === 1 ? 'a command' : `${count} commands`}`;
  if (key === 'documentSearch') return `searched documents`;
  if (key === 'listFiles') return `searched files`;
  if (key === 'web_search') return `searched the web`;
  if (key === 'readFile') return `read ${count === 1 ? 'a file' : `${count} files`}`;
  if (key === 'stageFileForShell') return `prepared ${count === 1 ? 'a file' : `${count} files`} for review`;
  if (key === 'loadSkill') return getSkillSummaryText(part);
  if (key === 'exposeFileToUser') return `prepared ${count === 1 ? 'a file' : `${count} files`}`;
  if (key === 'processFiles') return `processed files`;
  if (key === 'dataExplorer') return `explored data`;
  if (key === 'clarification') return `asked for clarification`;
  if (key === 'getMessage') return `read ${count === 1 ? 'an email' : `${count} emails`}`;
  if (key === 'listInbox' || key === 'listUnifiedMail') return `searched mail`;
  if (key === 'fillSl1Form') return `filled SL1 form`;
  if (key === 'fillSl2Form') return `filled SL2 form`;
  if (key === 'formFilling') return `filled ${count === 1 ? 'a form' : `${count} forms`}`;
  if (key === 'coverageGapAnalysis') return `analyzed coverage gaps`;
  if (key === 'image_generation') return `generated ${count === 1 ? 'an image' : `${count} images`}`;
  if (key === 'pptxRevisor') return `revised ${count === 1 ? 'a presentation' : `${count} presentations`}`;
  if (key === 'summarizeEntities') return `summarized records`;
  if (key === 'checkCapability') return `checked capability`;

  return `worked through ${count} ${count === 1 ? 'step' : 'steps'}`;
}

function getSkillSummaryPhrase(part: CoTStepBlockSummaryPart, sentenceStart: boolean): ReactNode {
  const verb = sentenceStart ? 'Used' : 'used';

  if (part.skillHandles?.length) {
    return (
      <>
        {verb}{' '}
        {part.skillHandles.map((handle, index) => (
          <Fragment key={handle}>
            {index > 0 ? ' ' : null}
            <code className="bg-muted rounded px-1 py-0.5 text-xs">{handle}</code>
          </Fragment>
        ))}
      </>
    );
  }

  return `${verb} ${part.count === 1 ? 'a skill' : `${part.count} skills`}`;
}

function getSkillSummaryText(part: CoTStepBlockSummaryPart): string {
  if (part.skillHandles?.length) {
    return `used ${part.skillHandles.join(' ')}`;
  }

  return `used ${part.count === 1 ? 'a skill' : `${part.count} skills`}`;
}

function getSkillHandle(step: CoTToolStep): string | undefined {
  const command = getField<string>(step.input, 'command');
  const legacySkillId = getField<string>(step.input, 'skillId');
  return formatSkillCommand(command) || legacySkillId;
}

function capitalizeFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export const MAX_OUTPUT_LENGTH = 500;

export type CoTSearchResult = { title: string; link?: string };

/** Safely retrieve a nested value from an unknown object. Returns undefined if the path doesn't match. */
export function getField<T>(obj: unknown, ...keys: string[]): T | undefined {
  const result = keys.reduce<unknown>(
    (current, key) =>
      current != null && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined,
    obj,
  );
  return result as T | undefined;
}
